import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import deepCopy from 'lib/deepCopy';
import * as ComplexPolymerActions from 'modbio/complex-polymers/ComplexPolymerActions';
import {
  ComplexPolymerConnection,
  ComplexPolymerType,
  ConjugateConnectionGraphQL,
  ConjugateGraphQL,
  ResolvedAttachmentGraphQL,
  EntityAssociations,
  SimplePolymerGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  resolvedAttachmentContainsHelmReference,
  generateEntityAssociationsFromStructure,
  getConnectionInputs,
  getEntityIds,
  getExistingResolvedAttachmentInputs,
} from 'modbio/complex-polymers/ComplexPolymerUtil';

import {getNodeById} from '../drag-and-drop/ComplexPolymerDragAndDropUtil';
import {PolymerStructureGraph} from '../PolymerStructureGraphConstants';

function getOrderedConnectionInputChain(conjugate: ConjugateGraphQL): Array<ConjugateConnectionGraphQL> {
  const connections: Array<ConjugateConnectionGraphQL> = [];

  connections.push(conjugate.connection);
  const connectionChain = connections.concat(
    conjugate.linkedMolecules.map((molecule) => molecule.connection)
  );
  return connectionChain;
}

function getConjugateConnectionsWithRemovedConjugate(
  connectionChain: Array<ConjugateConnectionGraphQL>,
  refToRemove: string
): Array<ConjugateConnectionGraphQL> {
  const firstConnectionToRemove = connectionChain.findIndex(
    (connection) => connection.target.polymerHelmReference === refToRemove
  );
  if (firstConnectionToRemove === -1) {
    return connectionChain;
  }

  return connectionChain.slice(0, firstConnectionToRemove);
}

function getAllConjugateChains(
  attachedConjugates: Array<ConjugateGraphQL>
): Array<Array<ConjugateConnectionGraphQL>> {
  const conjugateChains: Array<Array<ConjugateConnectionGraphQL>> = [];
  for (const conjugate of attachedConjugates) {
    const conjugateChain = getOrderedConnectionInputChain(conjugate);
    conjugateChains.push(conjugateChain);
  }
  return conjugateChains;
}

function getUpdatedEntityAssociations(
  currentEntityAssociations: EntityAssociations,
  selectedId: string,
  helmReference: string
): EntityAssociations {
  const newEntityAssociations: EntityAssociations = deepCopy(currentEntityAssociations);

  const polymerReferences = newEntityAssociations[selectedId];
  const selectedRefIndex = polymerReferences.findIndex((ref) => ref === helmReference);
  assert(selectedRefIndex !== -1, 'Expected to find polymer reference in entity associations');
  polymerReferences.splice(selectedRefIndex, 1);

  if (polymerReferences.length === 0) {
    delete newEntityAssociations[selectedId];
  } else {
    newEntityAssociations[selectedId] = polymerReferences;
  }

  return newEntityAssociations;
}

function getUpdatedStructureGraph(
  currentStructureGraph: PolymerStructureGraph,
  selectedRef: string
): PolymerStructureGraph {
  const newNodes = currentStructureGraph.nodes.filter((node) => node.data.polymerRef !== selectedRef);
  const newEdges = currentStructureGraph.edges.filter((edge) => {
    const sourcePolymerRef = getNodeById(edge.source, currentStructureGraph.nodes).data.polymerRef;
    const targetPolymerRef = getNodeById(edge.target, currentStructureGraph.nodes).data.polymerRef;

    return sourcePolymerRef !== selectedRef && targetPolymerRef !== selectedRef;
  });

  return {
    nodes: newNodes,
    edges: newEdges,
  };
}

export async function removeConjugate(
  complexPolymerId: string,
  complexPolymerVersionId: string,
  complexPolymerType: ComplexPolymerType,
  simplePolymers: Array<SimplePolymerGraphQL>,
  attachedConjugates: Array<ConjugateGraphQL>,
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>,
  selectedId: string,
  helmReference: string,
  structureGraph: PolymerStructureGraph | null,
  offset?: number
): Promise<void> {
  // Remove entity ID from existing list of entity IDs
  const entityIds = getEntityIds(simplePolymers);
  const oldIndex = entityIds.findIndex((id) => id === selectedId);
  assert(oldIndex !== -1, 'Expected to find entity ID to remove in existing entity IDs');

  const newEntityIds = deepCopy(entityIds);
  newEntityIds.splice(oldIndex, 1);

  // Get all conjugate chains and remove all conjugate connections from the selected entity onwards
  // TODO: Once we get to leaving group resolved attachments, we should simplify this function to simply
  // remove the resolved attachment which contains the conjugate being removed and remove the entire conjugate
  // chain.
  const allConjugateChains = getAllConjugateChains(attachedConjugates);
  const newConnections: Array<Array<ConjugateConnectionGraphQL>> = [];
  for (const conjugateChain of allConjugateChains) {
    newConnections.push(getConjugateConnectionsWithRemovedConjugate(conjugateChain, helmReference));
  }
  const newConnectionInputs = getConnectionInputs(newConnections.flat());

  const validResolvedAttachments = [];
  // Keep track of all connections that were associated with a resolved attachment that needs to be deleted.
  const additionalConnectionsToRemove: Array<ComplexPolymerConnection> = [];
  for (const resolvedAttachment of resolvedAttachments) {
    if (resolvedAttachmentContainsHelmReference(resolvedAttachment, helmReference)) {
      additionalConnectionsToRemove.push(...getConnectionInputs(resolvedAttachment.connectionsInvolved));
    } else {
      validResolvedAttachments.push(resolvedAttachment);
    }
  }
  const newResolvedAttachments = getExistingResolvedAttachmentInputs(validResolvedAttachments);
  // Filter out any connections which were in a deleted resolved attachment.
  const finalConnectionInputs = newConnectionInputs.filter(
    (connection) =>
      additionalConnectionsToRemove.find((connectionToRemove) => isEqual(connection, connectionToRemove)) ===
      undefined
  );

  // Remove HELM reference from entity associations
  const entityAssociations = generateEntityAssociationsFromStructure(simplePolymers);
  const newEntityAssociations = getUpdatedEntityAssociations(entityAssociations, selectedId, helmReference);

  const newStructureGraph =
    structureGraph != null ? getUpdatedStructureGraph(structureGraph, helmReference) : null;

  await ComplexPolymerActions.updateComplexPolymer(
    complexPolymerId,
    complexPolymerVersionId,
    newEntityIds,
    finalConnectionInputs,
    newResolvedAttachments,
    newEntityAssociations,
    complexPolymerType,
    offset,
    newStructureGraph ?? undefined
  );
}
